import { FC, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  FormControl,
  FormErrorMessage,
  HStack,
  Heading,
  Input,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { useNavigate, useParams } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import Select from 'react-select';

import countryCodes, { CountryProperty } from 'country-codes-list';

import {
  useContactInfoQuery,
  useCreateContactInfoMutation,
  useUpdateContactInfoMutation,
} from '../../../services';

import { ContactInfoForm } from '../models';
import { CenteredSpinner } from '../components';
import { getApiErrorMessage, getBodyFailureMessage } from '../utils';
import { BasicInfo } from '../../../models';

const options = countryCodes.all().map(country => ({
  value: country.countryCallingCode,
}));

export const ContactInfo: FC = () => {
  const [basicInfo, setBasicInfo] = useState<BasicInfo>();
  const [contactInfoId, setContactInfoId] = useState<number>();

  const navigate = useNavigate();

  const params = useParams();
  const { companyId } = params;
  const status = params.status || 'all';

  const { data: contactInfo, isLoading } = useContactInfoQuery(
    Number(companyId)
  );

  const [createContactInfo] = useCreateContactInfoMutation();
  const [updateContactInfo] = useUpdateContactInfoMutation();

  const toast = useToast();

  const showSaveError = (description: string) =>
    toast({
      title: 'Could not save contact info',
      description,
      status: 'error',
      duration: 9000,
      isClosable: true,
    });

  const showSaveSuccess = () =>
    toast({
      title: 'Contact info saved',
      status: 'success',
      duration: 4000,
      isClosable: true,
    });

  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
    control,
    reset,
  } = useForm<ContactInfoForm>();

  const navigateToPreviousPage = () =>
    navigate(`/companies/${status}/${companyId}`);

  async function onSubmit(data: ContactInfoForm) {
    try {
      // An empty array is truthy, so check for an existing contact, not the
      // array itself, to decide between create and update.
      const hasExistingContact = Boolean(contactInfo?.data?.[0]);

      const result = hasExistingContact
        ? await updateContactInfo({
            ...data,
            id: contactInfoId,
            companyBasicInfo: basicInfo,
            countryCode: data.countryCode.value,
          }).unwrap()
        : await createContactInfo({
            ...data,
            companyBasicInfo: { id: Number(companyId) },
            countryCode: data.countryCode.value,
          }).unwrap();

      // core-api reports business failures INSIDE a 200 body, so `.unwrap()`
      // resolves on them: classify the envelope (errorCode, canonical) before
      // treating this as saved. Body failures now reach the same error toast
      // that only transport failures used to reach.
      const failure = getBodyFailureMessage(result);
      if (failure) {
        showSaveError(failure);
        return;
      }
      showSaveSuccess();
      navigateToPreviousPage();
    } catch (error) {
      console.error(error);
      showSaveError(getApiErrorMessage(error));
    }
  }

  useEffect(() => {
    if (contactInfo?.data?.[0]) {
      const {
        companyBasicInfo,
        id,
        countryCode,
        email,
        firstName,
        jobTitle,
        lastName,
        phoneNumber,
      } = contactInfo.data[0];

      reset({
        countryCode: { value: countryCode },
        email,
        firstName,
        jobTitle,
        lastName,
        phoneNumber,
      });

      setBasicInfo(companyBasicInfo);
      setContactInfoId(id);
    }
  }, [contactInfo?.data, isLoading, reset]);

  if (isLoading) return <CenteredSpinner />;

  return (
    <Box>
      <HStack justify="space-between" mt="4" mb="12" align="start">
        <Heading color="custom_blue.1" fontSize="2xl">
          Contact Info
        </Heading>
      </HStack>

      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5">
        <HStack gap="4" align="flex-start">
          <VStack color="custom_blue.1" align="start" flexBasis="25%">
            <Box fontSize="sm" fontWeight="semibold">
              First Name
            </Box>
          </VStack>
          <FormControl isInvalid={!!errors.firstName} flexBasis="50%">
            <Input
              placeholder="First Name"
              {...register('firstName', {
                required: 'First name is required',
              })}
            />
            <FormErrorMessage>
              {errors.firstName && errors.firstName.message}
            </FormErrorMessage>
          </FormControl>
        </HStack>
        <Divider />

        <HStack gap="4" align="flex-start">
          <VStack color="custom_blue.1" align="start" flexBasis="25%">
            <Box fontSize="sm" fontWeight="semibold">
              Last Name
            </Box>
          </VStack>
          <FormControl isInvalid={!!errors.lastName} flexBasis="50%">
            <Input
              placeholder="Last Name"
              {...register('lastName', {
                required: 'Last name is required',
              })}
            />
            <FormErrorMessage>
              {errors.lastName && errors.lastName.message}
            </FormErrorMessage>
          </FormControl>
        </HStack>
        <Divider />

        <HStack gap="4" align="flex-start">
          <VStack color="custom_blue.1" align="start" flexBasis="25%">
            <Box fontSize="sm" fontWeight="semibold">
              Phone Number
            </Box>
          </VStack>
          <FormControl
            isInvalid={!!errors.phoneNumber || !!errors.countryCode}
            flexBasis="50%"
          >
            <HStack>
              <Box flexBasis="40%">
                <Controller
                  control={control}
                  name="countryCode"
                  rules={{
                    required: 'Country code is required',
                  }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      options={options}
                      getOptionLabel={({ value }) => value}
                      formatOptionLabel={({ value }) => {
                        const country = countryCodes.findOne(
                          'countryCallingCode' as CountryProperty,
                          value
                        );

                        return (
                          <div className="flex items-center">
                            <span
                              className={`fi fi-${country.countryCode.toLowerCase()} fis`}
                            ></span>
                            <span
                              style={{
                                marginLeft: 5,
                                width: '80px',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {value}
                            </span>
                          </div>
                        );
                      }}
                    />
                  )}
                />
              </Box>

              <Input
                placeholder="Phone number"
                {...register('phoneNumber', {
                  required: 'Phone number is required',
                })}
              />
            </HStack>
            <FormErrorMessage>
              {(errors.phoneNumber && errors.phoneNumber.message) ||
                (errors.countryCode && errors.countryCode.message)}
            </FormErrorMessage>
          </FormControl>
        </HStack>
        <Divider />

        <HStack gap="4" align="flex-start">
          <VStack color="custom_blue.1" align="start" flexBasis="25%">
            <Box fontSize="sm" fontWeight="semibold">
              Email
            </Box>
          </VStack>
          <FormControl isInvalid={!!errors.email} flexBasis="50%">
            <Input
              placeholder="Email"
              {...register('email', {
                required: 'Email is required',
              })}
            />
            <FormErrorMessage>
              {errors.email && errors.email.message}
            </FormErrorMessage>
          </FormControl>
        </HStack>
        <Divider />

        <HStack gap="4" align="flex-start">
          <VStack color="custom_blue.1" align="start" flexBasis="25%">
            <Box fontSize="sm" fontWeight="semibold">
              Role
            </Box>
          </VStack>
          <FormControl isInvalid={!!errors.jobTitle} flexBasis="50%">
            <Input
              placeholder="Job Title"
              {...register('jobTitle', {
                required: 'Job title is required',
              })}
            />
            <FormErrorMessage>
              {errors.jobTitle && errors.jobTitle.message}
            </FormErrorMessage>
          </FormControl>
        </HStack>

        <HStack mt={4} justify="end">
          <Button onClick={navigateToPreviousPage}>Cancel</Button>
          <Button
            isLoading={isSubmitting}
            type="submit"
            bg="custom_orange.2"
            _hover={{ bg: 'custom_orange.2' }}
            color="white"
          >
            Save
          </Button>
        </HStack>
      </form>
    </Box>
  );
};
